// GroupPhotoUploadModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Upload, Button, message, List } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { uploadGroupPhotos } from '../../api/group';

// Kept in sync with MAX_GROUP_PHOTOS in server/src/routers/group.js.
// Intentionally decoupled from the group member limit (up to 100 for
// production lines) to avoid an oversized Group document.
const MAX_GROUP_PHOTOS = 50;

const GroupPhotoUploadModal = ({ visible, onClose, group }) => {
  const [filesByMember, setFilesByMember] = useState({});
  const [uploading, setUploading] = useState(false);

  const members = group?.group || [];

  useEffect(() => {
    if (visible && members.length) {
      // Reset when modal opens
      const initial = {};
      members.forEach((m) => {
        initial[m.id_number] = null;
      });
      setFilesByMember(initial);
    }
  }, [visible, members]);

  const beforeUpload = (file) => {
    const isJpgOrPng =
      file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG files!');
      return Upload.LIST_IGNORE;
    }
    return false; // prevent auto upload
  };

  const handleMemberFileChange = (idNumber) => ({ fileList }) => {
    setFilesByMember((prev) => ({
      ...prev,
      [idNumber]: fileList[0] || null,
    }));
  };

  const handleUpload = async () => {
    if (!members.length) {
      message.error('No group members found to match photos to.');
      return;
    }

    // Only upload members that have a file selected; others are skipped
    const memberFiles = {};
    members.forEach((m) => {
      if (filesByMember[m.id_number]?.originFileObj) {
        memberFiles[m.id_number] = filesByMember[m.id_number].originFileObj;
      }
    });

    if (Object.keys(memberFiles).length === 0) {
      message.warning('Please select at least one photo to upload.');
      return;
    }

    setUploading(true);
    try {
      const data = await uploadGroupPhotos(group._id, memberFiles);
      const skippedForCap = data.skippedForCap || 0;
      const uploadedCount = Object.keys(memberFiles).length - skippedForCap;
      const skippedNoFile = members.length - Object.keys(memberFiles).length;

      if (skippedNoFile > 0 || skippedForCap > 0) {
        const parts = [`${uploadedCount} photo(s) uploaded.`];
        if (skippedNoFile > 0) {
          parts.push(`${skippedNoFile} member(s) skipped (no photo selected) — you can upload their photos later.`);
        }
        if (skippedForCap > 0) {
          parts.push(`${skippedForCap} photo(s) skipped: group photo limit of ${data.photoLimit || MAX_GROUP_PHOTOS} reached.`);
        }
        message.success(parts.join(' '));
      } else {
        message.success('All photos uploaded successfully!');
      }
      onClose();
    } catch (e) {
      message.error(e.message || 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={`Upload Group Photos${
        group?.group_name ? ` - ${group.group_name}` : ''
      }`}
      destroyOnClose
    >
      <p style={{ marginBottom: 12 }}>
        Upload one photo per member. You can upload for some members now and return later to add the rest.
      </p>
      <p style={{ marginBottom: 12, color: '#888' }}>
        {group?.profilePhotos?.length || 0} / {MAX_GROUP_PHOTOS} photos stored for this group.
      </p>

      <List
        dataSource={members}
        bordered
        renderItem={(member) => (
          <List.Item
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <div>
                <strong>{member.name}</strong>
              </div>
              <div style={{ fontSize: 12, color: '#888' }}>
                ID: {member.id_number}
              </div>
            </div>
            <div style={{ maxWidth: 260 }}>
              <Upload
                beforeUpload={beforeUpload}
                fileList={
                  filesByMember[member.id_number]
                    ? [filesByMember[member.id_number]]
                    : []
                }
                onChange={handleMemberFileChange(member.id_number)}
                multiple={false}
                accept=".jpg,.jpeg,.png"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />}>Select Photo</Button>
              </Upload>
            </div>
          </List.Item>
        )}
      />

      <div style={{ textAlign: 'right', marginTop: 24 }}>
        <Button
          type="primary"
          onClick={handleUpload}
          loading={uploading}
          disabled={!members.length}
          style={{ marginRight: 8 }}
        >
          Upload Photos
        </Button>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

export default GroupPhotoUploadModal;
